import type { DungeonDirection, DungeonRegion, DungeonRoomRole } from './dungeon';
import type { TilePoint } from './mobile';
import hallwaySliceEwModuleJson from '../../assets/editor/modules/classic-stone-hallway-slice-ew-v1.json';
import hallwaySliceNsModuleJson from '../../assets/editor/modules/classic-stone-hallway-slice-ns-v1.json';
import authoringRoomModuleJson from '../../assets/editor/modules/classic-stone-room-v1.json';

export type DungeonRotation = 0 | 90 | 180 | 270;
export const DUNGEON_MODULE_SIZE = 9;
export const DUNGEON_MIN_ROOM_SIZE = 7;
/**
 * One room may contribute at most 4,225 envelope cells. This keeps a maximal
 * chamber comfortably below the 750 KB generated-map payload budget while
 * bounding browser tile, collision, and automap work to a small fixed grid.
 */
export const DUNGEON_MAX_ROOM_SIZE = 65;
export const DUNGEON_MAX_MODULE_WEIGHT = 1_000;

export type DungeonRoomSizeClass = 'small' | 'standard' | 'large' | 'set-piece' | 'boss';

export function dungeonRoomSizeClassForDimensions(
  width: number,
  height: number,
): DungeonRoomSizeClass {
  const span = Math.max(width, height);
  if (span <= 7) return 'small';
  if (span <= 11) return 'standard';
  if (span <= 25) return 'large';
  if (span <= 41) return 'set-piece';
  return 'boss';
}

/** Future authored rooms may be rectangular, but odd dimensions keep a stable center tile. */
export function isValidDungeonRoomDimensions(width: number, height: number): boolean {
  return (
    Number.isInteger(width) &&
    Number.isInteger(height) &&
    width >= DUNGEON_MIN_ROOM_SIZE &&
    width <= DUNGEON_MAX_ROOM_SIZE &&
    height >= DUNGEON_MIN_ROOM_SIZE &&
    height <= DUNGEON_MAX_ROOM_SIZE &&
    width % 2 === 1 &&
    height % 2 === 1
  );
}

export interface DungeonModuleTransform {
  rotation: DungeonRotation;
  mirrored: boolean;
}

export interface DungeonModuleSocket {
  id: string;
  direction: DungeonDirection;
  /** Centered authored locator; the selected generator version owns the carved aperture width. */
  position: TilePoint;
  approach: TilePoint;
  seam: TilePoint;
  width: number;
  compatibility: string;
}

export type DungeonCorner = 'north-west' | 'north-east' | 'south-east' | 'south-west';

export type DungeonStructureRole =
  | { kind: 'wall'; direction: DungeonDirection }
  | { kind: 'outer-corner'; corner: DungeonCorner }
  | { kind: 'inner-corner'; corner: DungeonCorner }
  | { kind: 'doorway-return'; corner: DungeonCorner }
  | { kind: 'post' };

export interface DungeonModuleStatic {
  tileName: string;
  position: TilePoint;
  z: number;
  blocking: boolean;
  /** Authored wall face used for safe directional substitution under transforms. */
  wallDirection?: DungeonDirection;
  /** Wall pieces at a used socket are omitted when the module is assembled. */
  sealsSocket?: string;
  /** Explicit semantic art role for non-rectangular geometry and themed substitution. */
  structureRole?: DungeonStructureRole;
  /** ClassicUO light metadata carried through authored module assembly. */
  light?: {
    profile: number;
    color: number;
    shader?: number;
  };
  lightTransparent?: true;
}

export interface DungeonModuleMarker {
  kind: 'entrance' | 'exit' | 'landmark';
  position: TilePoint;
  tag?: string;
}

export interface DungeonModuleRepeat {
  /** Direction in which the one-tile authored cross-section is repeated. */
  direction: 'east-west' | 'north-south';
  /** Must match the sockets on the rooms joined by this hallway. */
  compatibility: string;
}

/** The serializable editor/runtime contract for one authored dungeon room module. */
export interface DungeonModuleDefinition {
  id: string;
  category: 'room' | 'corridor';
  width: number;
  height: number;
  /** Optional during legacy JSON migration; inferred from `walkable` when absent. */
  footprint?: TilePoint[];
  /** Selection/packing hint checked against the storage-envelope dimensions. */
  sizeClass?: DungeonRoomSizeClass;
  /** Exact non-floor cells that packing and corridor routing must keep clear. */
  clearanceHalo?: TilePoint[];
  weight: number;
  roles: DungeonRoomRole[];
  allowedTransforms: DungeonModuleTransform[];
  /** Present only on a repeatable 1x9 or 9x1 hallway cross-section. */
  repeat?: DungeonModuleRepeat;
  sockets: DungeonModuleSocket[];
  land: { tileName: string; position: TilePoint }[];
  statics: DungeonModuleStatic[];
  walkable: TilePoint[];
  revealRegions: DungeonRegion[];
  reservedZones: DungeonRegion[];
  /** Walkable regions kept clear for encounter entrances, exits, and presentation. */
  encounterZones?: DungeonRegion[];
  markers: DungeonModuleMarker[];
  /** Legacy authoring preview metadata; runtime structure comes from the selected kit. */
  wallArt?: Partial<Record<DungeonDirection, string>>;
}

export interface DungeonModuleValidation {
  ok: boolean;
  errors: string[];
}

const ALL_TRANSFORMS: DungeonModuleTransform[] = [
  { rotation: 0, mirrored: false },
  { rotation: 90, mirrored: false },
  { rotation: 180, mirrored: false },
  { rotation: 270, mirrored: false },
  { rotation: 0, mirrored: true },
  { rotation: 90, mirrored: true },
  { rotation: 180, mirrored: true },
  { rotation: 270, mirrored: true },
];

const SOCKETS: DungeonModuleSocket[] = [
  {
    id: 'north',
    direction: 'north',
    position: { x: 4, y: 0 },
    approach: { x: 4, y: 1 },
    seam: { x: 4, y: 0 },
    width: 1,
    compatibility: 'classic-stone-doorway-v1',
  },
  {
    id: 'east',
    direction: 'east',
    position: { x: 8, y: 4 },
    approach: { x: 7, y: 4 },
    seam: { x: 8, y: 4 },
    width: 1,
    compatibility: 'classic-stone-doorway-v1',
  },
  {
    id: 'south',
    direction: 'south',
    position: { x: 4, y: 8 },
    approach: { x: 4, y: 7 },
    seam: { x: 4, y: 8 },
    width: 1,
    compatibility: 'classic-stone-doorway-v1',
  },
  {
    id: 'west',
    direction: 'west',
    position: { x: 0, y: 4 },
    approach: { x: 1, y: 4 },
    seam: { x: 0, y: 4 },
    width: 1,
    compatibility: 'classic-stone-doorway-v1',
  },
];

const WALKABLE: TilePoint[] = [];
const LAND: { tileName: string; position: TilePoint }[] = [];
const WALLS: DungeonModuleStatic[] = [];
for (let y = 0; y < 9; y++) {
  for (let x = 0; x < 9; x++) {
    LAND.push({ tileName: `land-0x${(0x4eb + ((x + y * 3) % 4)).toString(16)}`, position: { x, y } });
    if (x > 0 && x < 8 && y > 0 && y < 8) WALKABLE.push({ x, y });
    if (y === 0 || y === 8) {
      WALLS.push({
        tileName: 'static-0x1c',
        position: { x, y },
        z: 0,
        blocking: true,
        wallDirection: y === 0 ? 'north' : 'south',
        ...(x === 4 ? { sealsSocket: y === 0 ? 'north' : 'south' } : {}),
      });
    } else if (x === 0 || x === 8) {
      WALLS.push({
        tileName: 'static-0x1d',
        position: { x, y },
        z: 0,
        blocking: true,
        wallDirection: x === 0 ? 'west' : 'east',
        ...(y === 4 ? { sealsSocket: x === 0 ? 'west' : 'east' } : {}),
      });
    }
  }
}

interface StoneModuleOptions {
  id: string;
  roles: DungeonRoomRole[];
  weight?: number;
  statics?: DungeonModuleStatic[];
  reservedZones?: DungeonRegion[];
  markers?: DungeonModuleMarker[];
  allowedTransforms?: DungeonModuleTransform[];
}

function stoneModule(options: StoneModuleOptions): DungeonModuleDefinition {
  return {
    id: options.id,
    category: 'room',
    width: 9,
    height: 9,
    weight: options.weight ?? 1,
    roles: options.roles,
    allowedTransforms: (options.allowedTransforms ?? ALL_TRANSFORMS).map((transform) => ({
      ...transform,
    })),
    sockets: SOCKETS.map((socket) => ({
      ...socket,
      position: { ...socket.position },
      approach: { ...socket.approach },
      seam: { ...socket.seam },
    })),
    land: LAND.map((tile) => ({ ...tile, position: { ...tile.position } })),
    statics: [...WALLS, ...(options.statics ?? [])].map((item) => ({
      ...item,
      position: { ...item.position },
    })),
    walkable: WALKABLE.filter(
      (point) =>
        !(options.statics ?? []).some(
          (item) => item.blocking && item.position.x === point.x && item.position.y === point.y,
        ),
    ).map((point) => ({ ...point })),
    revealRegions: [{ x: 0, y: 0, width: 9, height: 9 }],
    reservedZones: options.reservedZones ?? [{ x: 3, y: 3, width: 3, height: 3 }],
    markers: options.markers ?? [],
    wallArt: {
      north: 'static-0x1c',
      east: 'static-0x1d',
      south: 'static-0x1c',
      west: 'static-0x1d',
    },
  };
}

function corridorModule(direction: 'north-south' | 'east-west'): DungeonModuleDefinition {
  const vertical = direction === 'north-south';
  const land: DungeonModuleDefinition['land'] = [];
  const walkable: TilePoint[] = [];
  const statics: DungeonModuleStatic[] = [];
  for (let y = 0; y < DUNGEON_MODULE_SIZE; y++) {
    for (let x = 0; x < DUNGEON_MODULE_SIZE; x++) {
      const across = vertical ? x : y;
      if (across < 2 || across > 6) continue;
      land.push({
        tileName: `land-0x${(0x4eb + ((x + y * 3) % 4)).toString(16)}`,
        position: { x, y },
      });
      if (across >= 3 && across <= 5) walkable.push({ x, y });
      if (across === 2 || across === 6) {
        statics.push({
          tileName: vertical ? 'static-0x1d' : 'static-0x1c',
          position: { x, y },
          z: 0,
          blocking: true,
          wallDirection: vertical ? (x === 2 ? 'west' : 'east') : (y === 2 ? 'north' : 'south'),
        });
      }
    }
  }
  const sockets = vertical
    ? SOCKETS.filter((socket) => socket.direction === 'north' || socket.direction === 'south')
    : SOCKETS.filter((socket) => socket.direction === 'east' || socket.direction === 'west');
  for (const socket of sockets) {
    statics.push({
      tileName: vertical ? 'static-0x1c' : 'static-0x1d',
      position: { ...socket.position },
      z: 0,
      blocking: true,
      wallDirection: socket.direction,
      sealsSocket: socket.id,
    });
  }
  return {
    id: `classic-stone-corridor-${vertical ? 'ns' : 'ew'}-v1`,
    category: 'corridor',
    width: DUNGEON_MODULE_SIZE,
    height: DUNGEON_MODULE_SIZE,
    weight: 1,
    roles: ['ordinary'],
    allowedTransforms: [{ rotation: 0, mirrored: false }],
    sockets: sockets.map((socket) => ({
      ...socket,
      position: { ...socket.position },
      approach: { ...socket.approach },
      seam: { ...socket.seam },
    })),
    land,
    statics,
    walkable,
    revealRegions: [vertical
      ? { x: 3, y: 0, width: 3, height: DUNGEON_MODULE_SIZE }
      : { x: 0, y: 3, width: DUNGEON_MODULE_SIZE, height: 3 }],
    reservedZones: [],
    markers: [],
    wallArt: {
      north: 'static-0x1c',
      east: 'static-0x1d',
      south: 'static-0x1c',
      west: 'static-0x1d',
    },
  };
}

export const CLASSIC_STONE_ENTRANCE = stoneModule({
  id: 'classic-stone-entrance-v1',
  roles: ['entrance'],
  allowedTransforms: [{ rotation: 0, mirrored: false }],
  markers: [{ kind: 'entrance', position: { x: 4, y: 4 } }],
  statics: [
    { tileName: 'static-0xa05', position: { x: 2, y: 2 }, z: 0, blocking: false },
    { tileName: 'static-0xa05', position: { x: 6, y: 2 }, z: 0, blocking: false },
  ],
});

export const CLASSIC_STONE_EXIT = stoneModule({
  id: 'classic-stone-exit-v1',
  roles: ['exit'],
  markers: [{ kind: 'exit', position: { x: 4, y: 4 } }],
  statics: [
    { tileName: 'static-0xdb', position: { x: 2, y: 6 }, z: 0, blocking: true },
    { tileName: 'static-0xdb', position: { x: 6, y: 6 }, z: 0, blocking: true },
  ],
});

export const CLASSIC_STONE_LANDMARK = stoneModule({
  id: 'classic-stone-landmark-v1',
  roles: ['landmark'],
  allowedTransforms: [{ rotation: 0, mirrored: false }],
  markers: [{ kind: 'landmark', position: { x: 4, y: 4 }, tag: 'standing-stone' }],
  statics: [
    { tileName: 'static-0x1363', position: { x: 4, y: 4 }, z: 0, blocking: true },
    { tileName: 'static-0xa05', position: { x: 2, y: 2 }, z: 0, blocking: false },
  ],
});

export const CLASSIC_STONE_JUNCTION = stoneModule({
  id: 'classic-stone-junction-v1',
  roles: ['junction'],
  weight: 2,
  statics: [
    { tileName: 'static-0xdb', position: { x: 2, y: 2 }, z: 0, blocking: true },
    { tileName: 'static-0xdb', position: { x: 6, y: 6 }, z: 0, blocking: true },
  ],
});

export const CLASSIC_STONE_ROOM = stoneModule({
  id: 'classic-stone-room-v1',
  roles: ['ordinary', 'dead-end'],
  weight: 3,
});

export const CLASSIC_STONE_ALCOVE = stoneModule({
  id: 'classic-stone-alcove-v1',
  roles: ['ordinary', 'dead-end'],
  allowedTransforms: [{ rotation: 0, mirrored: false }],
  statics: [
    { tileName: 'static-0xdb', position: { x: 2, y: 2 }, z: 0, blocking: true },
    { tileName: 'static-0xa05', position: { x: 6, y: 6 }, z: 0, blocking: false },
  ],
});

export const CLASSIC_STONE_CORRIDOR_NS = corridorModule('north-south');
export const CLASSIC_STONE_CORRIDOR_EW = corridorModule('east-west');

function semanticRoomLand(points: readonly TilePoint[]): DungeonModuleDefinition['land'] {
  return points.map((position, index) => ({
    tileName: `land-0x${(0x4eb + (index % 4)).toString(16)}`,
    position: { ...position },
  }));
}

function revealRows(points: readonly TilePoint[]): DungeonRegion[] {
  const rows = new Map<number, number[]>();
  for (const point of points) {
    const xs = rows.get(point.y) ?? [];
    xs.push(point.x);
    rows.set(point.y, xs);
  }
  return [...rows]
    .sort(([a], [b]) => a - b)
    .map(([y, xs]) => ({
      x: Math.min(...xs),
      y,
      width: Math.max(...xs) - Math.min(...xs) + 1,
      height: 1,
    }));
}

function withDerivedClearance(module: DungeonModuleDefinition): DungeonModuleDefinition {
  return { ...module, clearanceHalo: deriveDungeonClearanceHalo(module) };
}

interface CombatRoomOptions {
  id: string;
  roles: DungeonRoomRole[];
  marker?: DungeonModuleMarker;
  statics?: DungeonModuleStatic[];
  weight?: number;
}

const COMBAT_ROOM_SIZE = 33;
const COMBAT_ROOM_CENTER = Math.floor(COMBAT_ROOM_SIZE / 2);
const COMBAT_ROOM_FOOTPRINT = Array.from(
  { length: (COMBAT_ROOM_SIZE - 2) ** 2 },
  (_, index) => ({
    x: 1 + (index % (COMBAT_ROOM_SIZE - 2)),
    y: 1 + Math.floor(index / (COMBAT_ROOM_SIZE - 2)),
  }),
);

function combatRoom(options: CombatRoomOptions): DungeonModuleDefinition {
  const statics = options.statics ?? [];
  const sockets: DungeonModuleSocket[] = [
    {
      id: 'north',
      direction: 'north',
      position: { x: COMBAT_ROOM_CENTER, y: 0 },
      approach: { x: COMBAT_ROOM_CENTER, y: 1 },
      seam: { x: COMBAT_ROOM_CENTER, y: 0 },
      width: 1,
      compatibility: 'classic-stone-doorway-v1',
    },
    {
      id: 'east',
      direction: 'east',
      position: { x: COMBAT_ROOM_SIZE - 1, y: COMBAT_ROOM_CENTER },
      approach: { x: COMBAT_ROOM_SIZE - 2, y: COMBAT_ROOM_CENTER },
      seam: { x: COMBAT_ROOM_SIZE - 1, y: COMBAT_ROOM_CENTER },
      width: 1,
      compatibility: 'classic-stone-doorway-v1',
    },
    {
      id: 'south',
      direction: 'south',
      position: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_SIZE - 1 },
      approach: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_SIZE - 2 },
      seam: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_SIZE - 1 },
      width: 1,
      compatibility: 'classic-stone-doorway-v1',
    },
    {
      id: 'west',
      direction: 'west',
      position: { x: 0, y: COMBAT_ROOM_CENTER },
      approach: { x: 1, y: COMBAT_ROOM_CENTER },
      seam: { x: 0, y: COMBAT_ROOM_CENTER },
      width: 1,
      compatibility: 'classic-stone-doorway-v1',
    },
  ];
  return withDerivedClearance({
    id: options.id,
    category: 'room',
    width: COMBAT_ROOM_SIZE,
    height: COMBAT_ROOM_SIZE,
    sizeClass: 'set-piece',
    footprint: COMBAT_ROOM_FOOTPRINT,
    weight: options.weight ?? 1,
    roles: options.roles,
    allowedTransforms: ALL_TRANSFORMS,
    sockets,
    land: semanticRoomLand(COMBAT_ROOM_FOOTPRINT),
    statics,
    walkable: COMBAT_ROOM_FOOTPRINT.filter(
      (point) =>
        !statics.some(
          (item) =>
            item.blocking &&
            item.position.x === point.x &&
            item.position.y === point.y,
        ),
    ),
    revealRegions: [{
      x: 1,
      y: 1,
      width: COMBAT_ROOM_SIZE - 2,
      height: COMBAT_ROOM_SIZE - 2,
    }],
    reservedZones: [{
      x: COMBAT_ROOM_CENTER - 6,
      y: COMBAT_ROOM_CENTER - 6,
      width: 13,
      height: 13,
    }],
    encounterZones: [{
      x: COMBAT_ROOM_CENTER - 5,
      y: 3,
      width: 11,
      height: 5,
    }],
    markers: options.marker ? [options.marker] : [],
  });
}

export const CLASSIC_STONE_COMBAT_ENTRANCE = combatRoom({
  id: 'classic-stone-combat-entrance-v1',
  roles: ['entrance'],
  marker: {
    kind: 'entrance',
    position: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_CENTER },
  },
});

export const CLASSIC_STONE_COMBAT_ROOM = combatRoom({
  id: 'classic-stone-combat-room-v1',
  roles: ['ordinary', 'dead-end'],
  weight: 8,
});

export const CLASSIC_STONE_COMBAT_JUNCTION = combatRoom({
  id: 'classic-stone-combat-junction-v1',
  roles: ['junction'],
  weight: 2,
});

export const CLASSIC_STONE_COMBAT_LANDMARK = combatRoom({
  id: 'classic-stone-combat-landmark-v1',
  roles: ['landmark'],
  marker: {
    kind: 'landmark',
    position: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_CENTER },
    tag: 'standing-stone',
  },
  statics: [{
    tileName: 'static-0x1363',
    position: { x: COMBAT_ROOM_CENTER, y: COMBAT_ROOM_CENTER },
    z: 0,
    blocking: true,
  }],
});

const ROUND_ROOM_SIZE = 33;
const ROUND_ROOM_CENTER = Math.floor(ROUND_ROOM_SIZE / 2);
const ROUND_ROOM_RADIUS = 14;
const ROUND_FOOTPRINT = Array.from({ length: ROUND_ROOM_SIZE ** 2 }, (_, index) => ({
  x: index % ROUND_ROOM_SIZE,
  y: Math.floor(index / ROUND_ROOM_SIZE),
})).filter(
  ({ x, y }) =>
    (x - ROUND_ROOM_CENTER) ** 2 + (y - ROUND_ROOM_CENTER) ** 2 <=
    ROUND_ROOM_RADIUS ** 2,
);

/** Tile-set-independent round geometry rendered through the selected structure kit. */
export const CLASSIC_STONE_ROUND_ROOM = withDerivedClearance({
  id: 'classic-stone-round-room-v1',
  category: 'room',
  width: ROUND_ROOM_SIZE,
  height: ROUND_ROOM_SIZE,
  sizeClass: 'set-piece',
  footprint: ROUND_FOOTPRINT,
  weight: 2,
  roles: ['ordinary', 'dead-end'],
  allowedTransforms: ALL_TRANSFORMS,
  sockets: [
    { id: 'north', direction: 'north', position: { x: ROUND_ROOM_CENTER, y: 1 }, approach: { x: ROUND_ROOM_CENTER, y: 2 }, seam: { x: ROUND_ROOM_CENTER, y: 1 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'east', direction: 'east', position: { x: ROUND_ROOM_SIZE - 2, y: ROUND_ROOM_CENTER }, approach: { x: ROUND_ROOM_SIZE - 3, y: ROUND_ROOM_CENTER }, seam: { x: ROUND_ROOM_SIZE - 2, y: ROUND_ROOM_CENTER }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'south', direction: 'south', position: { x: ROUND_ROOM_CENTER, y: ROUND_ROOM_SIZE - 2 }, approach: { x: ROUND_ROOM_CENTER, y: ROUND_ROOM_SIZE - 3 }, seam: { x: ROUND_ROOM_CENTER, y: ROUND_ROOM_SIZE - 2 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'west', direction: 'west', position: { x: 1, y: ROUND_ROOM_CENTER }, approach: { x: 2, y: ROUND_ROOM_CENTER }, seam: { x: 1, y: ROUND_ROOM_CENTER }, width: 1, compatibility: 'classic-stone-doorway-v1' },
  ],
  land: semanticRoomLand(ROUND_FOOTPRINT),
  statics: [],
  walkable: ROUND_FOOTPRINT,
  revealRegions: revealRows(ROUND_FOOTPRINT),
  reservedZones: [{ x: 10, y: 10, width: 13, height: 13 }],
  encounterZones: [{ x: 11, y: 5, width: 11, height: 5 }],
  markers: [],
});

const BOSS_FOOTPRINT = Array.from({ length: 41 * 41 }, (_, index) => ({
  x: 1 + (index % 41),
  y: 1 + Math.floor(index / 41),
}));

/** A future-combat chamber whose large reservations are already generation-safe. */
export const CLASSIC_STONE_BOSS_ROOM = withDerivedClearance({
  id: 'classic-stone-boss-room-v1',
  category: 'room',
  width: 43,
  height: 43,
  sizeClass: 'boss',
  footprint: BOSS_FOOTPRINT,
  weight: 1,
  roles: ['exit'],
  allowedTransforms: ALL_TRANSFORMS,
  sockets: [
    { id: 'north', direction: 'north', position: { x: 21, y: 0 }, approach: { x: 21, y: 1 }, seam: { x: 21, y: 0 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'east', direction: 'east', position: { x: 42, y: 21 }, approach: { x: 41, y: 21 }, seam: { x: 42, y: 21 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'south', direction: 'south', position: { x: 21, y: 42 }, approach: { x: 21, y: 41 }, seam: { x: 21, y: 42 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
    { id: 'west', direction: 'west', position: { x: 0, y: 21 }, approach: { x: 1, y: 21 }, seam: { x: 0, y: 21 }, width: 1, compatibility: 'classic-stone-doorway-v1' },
  ],
  land: semanticRoomLand(BOSS_FOOTPRINT),
  statics: [],
  walkable: BOSS_FOOTPRINT,
  revealRegions: [{ x: 1, y: 1, width: 41, height: 41 }],
  reservedZones: [{ x: 9, y: 9, width: 25, height: 25 }],
  encounterZones: [{ x: 18, y: 4, width: 7, height: 5 }],
  markers: [{ kind: 'exit', position: { x: 21, y: 21 } }],
});

function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) return value;
  for (const nested of Object.values(value)) deepFreeze(nested);
  return Object.freeze(value);
}

const PRODUCTION_ROOMS = [
  CLASSIC_STONE_COMBAT_ENTRANCE,
  CLASSIC_STONE_COMBAT_ROOM,
  CLASSIC_STONE_COMBAT_JUNCTION,
  CLASSIC_STONE_COMBAT_LANDMARK,
  CLASSIC_STONE_BOSS_ROOM,
] as const;

/** The compact source room used by the editor's structure-kit conformance preview. */
export const CLASSIC_STONE_AUTHORING_ROOM = deepFreeze(
  structuredClone(authoringRoomModuleJson) as DungeonModuleDefinition,
);

/** Current authored corpus for generation, editor candidate checks, and author-facing tooling. */
export const DUNGEON_MODULES: readonly DungeonModuleDefinition[] = deepFreeze(
  structuredClone([
    ...PRODUCTION_ROOMS,
    hallwaySliceNsModuleJson,
    hallwaySliceEwModuleJson,
  ]) as DungeonModuleDefinition[],
);

function pointInside(module: DungeonModuleDefinition, point: TilePoint): boolean {
  return (
    Number.isInteger(point.x) &&
    Number.isInteger(point.y) &&
    point.x >= 0 &&
    point.y >= 0 &&
    point.x < module.width &&
    point.y < module.height
  );
}

/** Resolve the semantic floor independently of the rectangular storage envelope. */
export function dungeonModuleFootprint(module: DungeonModuleDefinition): readonly TilePoint[] {
  if (module.footprint) return module.footprint;
  if (module.category === 'corridor') return module.walkable;
  const legacyRectangle: TilePoint[] = [];
  for (let y = 1; y < module.height - 1; y++) {
    for (let x = 1; x < module.width - 1; x++) legacyRectangle.push({ x, y });
  }
  return legacyRectangle;
}

/** Derive the complete in-envelope one-cell dilation ring around semantic floor. */
export function deriveDungeonClearanceHalo(module: DungeonModuleDefinition): TilePoint[] {
  const floor = new Set(dungeonModuleFootprint(module).map((point) => `${point.x},${point.y}`));
  const halo = new Map<string, TilePoint>();
  for (const point of dungeonModuleFootprint(module)) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const candidate = { x: point.x + dx, y: point.y + dy };
        const candidateKey = `${candidate.x},${candidate.y}`;
        if (!pointInside(module, candidate) || floor.has(candidateKey)) continue;
        halo.set(candidateKey, candidate);
      }
    }
  }
  return [...halo.values()].sort((a, b) => a.y - b.y || a.x - b.x);
}

export function isDungeonStructureRole(value: unknown): value is DungeonStructureRole {
  if (typeof value !== 'object' || value === null) return false;
  const role = value as { kind?: unknown; direction?: unknown; corner?: unknown };
  if (role.kind === 'post') return true;
  if (role.kind === 'wall') {
    return role.direction === 'north' || role.direction === 'east' ||
      role.direction === 'south' || role.direction === 'west';
  }
  if (
    role.kind !== 'outer-corner' &&
    role.kind !== 'inner-corner' &&
    role.kind !== 'doorway-return'
  ) {
    return false;
  }
  return role.corner === 'north-west' || role.corner === 'north-east' ||
    role.corner === 'south-east' || role.corner === 'south-west';
}

function regionInside(module: DungeonModuleDefinition, region: DungeonRegion): boolean {
  return (
    Number.isInteger(region.x) &&
    Number.isInteger(region.y) &&
    Number.isInteger(region.width) &&
    Number.isInteger(region.height) &&
    region.width > 0 &&
    region.height > 0 &&
    region.x >= 0 &&
    region.y >= 0 &&
    region.x + region.width <= module.width &&
    region.y + region.height <= module.height
  );
}

function regionCells(region: DungeonRegion): TilePoint[] {
  const cells: TilePoint[] = [];
  for (let y = region.y; y < region.y + region.height; y++) {
    for (let x = region.x; x < region.x + region.width; x++) cells.push({ x, y });
  }
  return cells;
}

/** Validate authored geometry and socket semantics before generation consumes them. */
export function validateDungeonModule(module: DungeonModuleDefinition): DungeonModuleValidation {
  const errors: string[] = [];
  const repeatSlice = module.category === 'corridor' && module.repeat !== undefined;
  if (!module.id.trim()) errors.push('module id is required');
  if (module.category !== 'room' && module.category !== 'corridor') {
    errors.push('module category must be room or corridor');
  }
  if (repeatSlice) {
    const validDirection =
      module.repeat?.direction === 'east-west' || module.repeat?.direction === 'north-south';
    if (!validDirection) errors.push('repeatable hallway has an invalid direction');
    const expectedWidth = module.repeat?.direction === 'east-west' ? 1 : DUNGEON_MODULE_SIZE;
    const expectedHeight = module.repeat?.direction === 'east-west' ? DUNGEON_MODULE_SIZE : 1;
    if (module.width !== expectedWidth || module.height !== expectedHeight) {
      errors.push(
        `repeatable ${module.repeat?.direction ?? 'unknown'} hallway must be ${expectedWidth}x${expectedHeight}`,
      );
    }
    if (!module.repeat?.compatibility.trim()) errors.push('repeatable hallway has no compatibility');
    if (module.sockets.length !== 0) errors.push('repeatable hallway must not declare sockets');
    if (
      module.allowedTransforms.length !== 1 ||
      module.allowedTransforms[0]?.rotation !== 0 ||
      module.allowedTransforms[0]?.mirrored !== false
    ) {
      errors.push('repeatable hallway must use only the identity transform');
    }
  } else if (module.category === 'room') {
    if (!isValidDungeonRoomDimensions(module.width, module.height)) {
      errors.push(
        `room dimensions must be odd integers between ${DUNGEON_MIN_ROOM_SIZE} and ${DUNGEON_MAX_ROOM_SIZE}`,
      );
    }
    const inferredSizeClass = dungeonRoomSizeClassForDimensions(module.width, module.height);
    if (Math.max(module.width, module.height) > 25 && module.sizeClass === undefined) {
      errors.push('rooms larger than 25 tiles must declare a size class');
    }
    if (module.sizeClass !== undefined && module.sizeClass !== inferredSizeClass) {
      errors.push(`room size class must be ${inferredSizeClass}`);
    }
    if (module.repeat !== undefined) {
      errors.push('room module cannot be repeatable');
    }
  } else {
    if (module.width !== DUNGEON_MODULE_SIZE) errors.push(`width must be ${DUNGEON_MODULE_SIZE}`);
    if (module.height !== DUNGEON_MODULE_SIZE) errors.push(`height must be ${DUNGEON_MODULE_SIZE}`);
  }
  if (
    !Number.isFinite(module.weight) ||
    module.weight <= 0 ||
    module.weight > DUNGEON_MAX_MODULE_WEIGHT
  ) {
    errors.push(`weight must be between 0 and ${DUNGEON_MAX_MODULE_WEIGHT}`);
  }
  if (module.roles.length === 0) errors.push('at least one role is required');
  if (module.allowedTransforms.length === 0) errors.push('at least one transform is required');
  const transforms = new Set<string>();
  for (const transform of module.allowedTransforms) {
    if (
      (transform.rotation !== 0 &&
        transform.rotation !== 90 &&
        transform.rotation !== 180 &&
        transform.rotation !== 270) ||
      typeof transform.mirrored !== 'boolean'
    ) {
      errors.push('transform must use rotation 0, 90, 180, or 270 and a boolean mirror');
      continue;
    }
    const transformKey = `${transform.rotation}:${transform.mirrored}`;
    if (transforms.has(transformKey)) errors.push(`duplicate transform: ${transformKey}`);
    transforms.add(transformKey);
  }
  const pointKey = (point: TilePoint): string => `${point.x},${point.y}`;
  const footprintPoints = dungeonModuleFootprint(module);
  const footprint = new Set(footprintPoints.map(pointKey));
  if (footprintPoints.length === 0) errors.push('semantic footprint is required');
  if (footprint.size !== footprintPoints.length) errors.push('semantic footprint has duplicate cells');
  for (const point of footprintPoints) {
    if (!pointInside(module, point)) errors.push('semantic footprint cell is outside the module');
  }
  if (
    module.category === 'room' &&
    footprintPoints.some(
      (point) =>
        point.x === 0 ||
        point.y === 0 ||
        point.x === module.width - 1 ||
        point.y === module.height - 1,
    )
  ) {
    errors.push('semantic footprint must leave one-cell clearance inside the storage envelope');
  }
  const firstFloor = footprintPoints[0];
  if (firstFloor) {
    const reached = new Set([pointKey(firstFloor)]);
    const pending = [{ ...firstFloor }];
    while (pending.length > 0) {
      const current = pending.shift()!;
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
        const next = { x: current.x + dx, y: current.y + dy };
        const nextKey = pointKey(next);
        if (reached.has(nextKey) || !footprint.has(nextKey)) continue;
        reached.add(nextKey);
        pending.push(next);
      }
    }
    if (reached.size !== footprint.size) errors.push('semantic footprint must be connected');
  }
  const clearancePoints = module.clearanceHalo ?? [];
  const clearance = new Set(clearancePoints.map(pointKey));
  if (clearance.size !== clearancePoints.length) errors.push('clearance halo has duplicate cells');
  for (const point of clearancePoints) {
    if (!pointInside(module, point)) errors.push('clearance halo cell is outside the module');
    if (footprint.has(pointKey(point))) errors.push('clearance halo overlaps the semantic footprint');
  }
  if (module.clearanceHalo !== undefined) {
    const expectedClearance = deriveDungeonClearanceHalo(module);
    if (
      expectedClearance.length !== clearance.size ||
      expectedClearance.some((point) => !clearance.has(pointKey(point)))
    ) {
      errors.push('clearance halo must contain the complete one-tile dilation ring');
    }
  }
  const walkable = new Set(module.walkable.map(pointKey));
  const ids = new Set<string>();
  for (const socket of module.sockets) {
    if (ids.has(socket.id)) errors.push(`duplicate socket id: ${socket.id}`);
    ids.add(socket.id);
    if (socket.width !== 1) {
      errors.push(`socket ${socket.id} width must be 1`);
    }
    if (!socket.compatibility.trim()) errors.push(`socket ${socket.id} has no compatibility`);
    const inBounds = pointInside(module, socket.position);
    if (!inBounds) errors.push(`socket ${socket.id} is out of bounds`);
    const { x, y } = socket.position;
    if (
      socket.direction !== 'north' &&
      socket.direction !== 'east' &&
      socket.direction !== 'south' &&
      socket.direction !== 'west'
    ) {
      errors.push(`socket ${socket.id} has an invalid direction`);
    }
    const onDeclaredEdge =
      !footprint.has(pointKey(socket.position)) && footprint.has(pointKey(socket.approach));
    if (inBounds && !onDeclaredEdge) errors.push(`socket ${socket.id} is not on its declared edge`);
    if (!pointInside(module, socket.approach) || !walkable.has(`${socket.approach.x},${socket.approach.y}`)) {
      errors.push(`socket ${socket.id} has no walkable approach`);
    }
    if (
      !pointInside(module, socket.seam) ||
      socket.seam.x !== socket.position.x ||
      socket.seam.y !== socket.position.y
    ) {
      errors.push(`socket ${socket.id} seam must match its boundary position`);
    }
    const expectedApproach =
      socket.direction === 'north'
        ? { x, y: y + 1 }
        : socket.direction === 'east'
          ? { x: x - 1, y }
          : socket.direction === 'south'
            ? { x, y: y - 1 }
            : { x: x + 1, y };
    if (
      socket.approach.x !== expectedApproach.x ||
      socket.approach.y !== expectedApproach.y
    ) {
      errors.push(`socket ${socket.id} approach must be one tile inward`);
    }
  }
  if (!repeatSlice && module.sockets.length === 0) errors.push('at least one socket is required');
  for (const point of module.walkable) {
    if (!pointInside(module, point)) errors.push('walkable cell is outside the module');
    if (!footprint.has(pointKey(point))) errors.push('walkable cell is outside the semantic footprint');
  }
  const landCells = new Set(module.land.map((tile) => `${tile.position.x},${tile.position.y}`));
  for (const point of module.walkable) {
    if (!landCells.has(`${point.x},${point.y}`)) {
      errors.push('walkable cell has no authored land');
    }
  }
  for (const point of footprintPoints) {
    if (!landCells.has(pointKey(point))) errors.push('semantic footprint cell has no authored land');
  }
  for (const tile of module.land) {
    if (!/^land-0x[0-9a-f]+$/.test(tile.tileName) || !pointInside(module, tile.position)) {
      errors.push('land tile is invalid');
    }
  }
  for (const item of module.statics) {
    if (!/^static-0x[0-9a-f]+$/.test(item.tileName) || !pointInside(module, item.position)) {
      errors.push('static is invalid');
    }
    if (item.sealsSocket && !ids.has(item.sealsSocket)) {
      errors.push(`static seals unknown socket ${item.sealsSocket}`);
    }
    if (item.structureRole !== undefined && !isDungeonStructureRole(item.structureRole)) {
      errors.push('static structure role is invalid');
    }
    if (
      item.light !== undefined &&
      (
        !Number.isInteger(item.light.profile) ||
        item.light.profile < 0 ||
        item.light.profile >= 100 ||
        !Number.isInteger(item.light.color) ||
        item.light.color < 0 ||
        item.light.color > 0xffffff ||
        (
          item.light.shader !== undefined &&
          (
            !Number.isInteger(item.light.shader) ||
            item.light.shader < 0 ||
            item.light.shader > 63
          )
        )
      )
    ) {
      errors.push('static light is invalid');
    }
    if (item.lightTransparent !== undefined && item.lightTransparent !== true) {
      errors.push('static light transparency is invalid');
    }
  }
  const blocking = new Set(
    module.statics
      .filter((item) => item.blocking)
      .map((item) => `${item.position.x},${item.position.y}`),
  );
  for (const socket of module.sockets) {
    if (blocking.has(`${socket.approach.x},${socket.approach.y}`)) {
      errors.push(`socket ${socket.id} approach is blocked by a static`);
    }
  }
  const firstApproach = module.sockets[0]?.approach;
  if (firstApproach) {
    const reached = new Set([`${firstApproach.x},${firstApproach.y}`]);
    const pending = [{ ...firstApproach }];
    while (pending.length > 0) {
      const current = pending.shift()!;
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
        const next = { x: current.x + dx, y: current.y + dy };
        const nextKey = `${next.x},${next.y}`;
        if (reached.has(nextKey) || !walkable.has(nextKey) || blocking.has(nextKey)) continue;
        reached.add(nextKey);
        pending.push(next);
      }
    }
    for (const socket of module.sockets) {
      if (!reached.has(`${socket.approach.x},${socket.approach.y}`)) {
        errors.push(`socket ${socket.id} approach is disconnected`);
      }
    }
  }
  for (const zone of module.revealRegions) {
    if (!regionInside(module, zone)) errors.push('reveal region is outside the module');
    else if (
      module.footprint !== undefined &&
      regionCells(zone).some((point) => !footprint.has(pointKey(point)))
    ) {
      errors.push('reveal region is outside the semantic footprint');
    }
  }
  for (const zone of module.reservedZones) {
    if (!regionInside(module, zone)) errors.push('reserved zone is outside the module');
    else if (regionCells(zone).some((point) => !footprint.has(pointKey(point)))) {
      errors.push('reserved zone is outside the semantic footprint');
    }
  }
  for (const zone of module.encounterZones ?? []) {
    if (!regionInside(module, zone)) errors.push('encounter zone is outside the module');
    else if (regionCells(zone).some((point) => !footprint.has(pointKey(point)))) {
      errors.push('encounter zone is outside the semantic footprint');
    } else if (regionCells(zone).some((point) => !walkable.has(pointKey(point)))) {
      errors.push('encounter zone is not fully walkable');
    }
  }
  for (const marker of module.markers) {
    if (!pointInside(module, marker.position)) errors.push(`${marker.kind} marker is outside the module`);
    else if (!footprint.has(pointKey(marker.position))) {
      errors.push(`${marker.kind} marker is outside the semantic footprint`);
    }
  }
  for (const role of module.roles) {
    if (
      (role === 'entrance' || role === 'exit' || role === 'landmark') &&
      !module.markers.some((marker) => marker.kind === role)
    ) {
      errors.push(`${role} module has no ${role} marker`);
    }
  }
  return { ok: errors.length === 0, errors };
}

export function dungeonModulesForRole(role: DungeonRoomRole): DungeonModuleDefinition[] {
  return DUNGEON_MODULES.filter((module) => module.roles.includes(role));
}

function rotateDirection(direction: DungeonDirection, rotation: DungeonRotation): DungeonDirection {
  const order: DungeonDirection[] = ['north', 'east', 'south', 'west'];
  return order[(order.indexOf(direction) + rotation / 90) % 4]!;
}

export function transformDungeonDirection(
  direction: DungeonDirection,
  transform: DungeonModuleTransform,
): DungeonDirection {
  const mirrored = transform.mirrored
    ? direction === 'east'
      ? 'west'
      : direction === 'west'
        ? 'east'
        : direction
    : direction;
  return rotateDirection(mirrored, transform.rotation);
}

export function transformDungeonPoint(
  module: Pick<DungeonModuleDefinition, 'width' | 'height'>,
  point: TilePoint,
  transform: DungeonModuleTransform,
): TilePoint {
  const x = transform.mirrored ? module.width - 1 - point.x : point.x;
  const y = point.y;
  switch (transform.rotation) {
    case 0:
      return { x, y };
    case 90:
      return { x: module.height - 1 - y, y: x };
    case 180:
      return { x: module.width - 1 - x, y: module.height - 1 - y };
    case 270:
      return { x: y, y: module.width - 1 - x };
  }
}

export function transformDungeonRegion(
  module: DungeonModuleDefinition,
  region: DungeonRegion,
  transform: DungeonModuleTransform,
): DungeonRegion {
  const corners = [
    { x: region.x, y: region.y },
    { x: region.x + region.width - 1, y: region.y },
    { x: region.x, y: region.y + region.height - 1 },
    { x: region.x + region.width - 1, y: region.y + region.height - 1 },
  ].map((point) => transformDungeonPoint(module, point, transform));
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs) + 1,
    height: Math.max(...ys) - Math.min(...ys) + 1,
  };
}

/** Transform any exact authored point set without widening it to a region. */
export function transformDungeonPoints(
  module: DungeonModuleDefinition,
  points: readonly TilePoint[],
  transform: DungeonModuleTransform,
): TilePoint[] {
  return points.map((point) => transformDungeonPoint(module, point, transform));
}

/** Transform exact semantic floor cells without expanding them to a rectangle. */
export function transformDungeonFootprint(
  module: DungeonModuleDefinition,
  transform: DungeonModuleTransform,
): TilePoint[] {
  return transformDungeonPoints(module, dungeonModuleFootprint(module), transform);
}

export function transformedDungeonSocket(
  module: DungeonModuleDefinition,
  socket: DungeonModuleSocket,
  transform: DungeonModuleTransform,
): DungeonModuleSocket {
  return {
    ...socket,
    direction: transformDungeonDirection(socket.direction, transform),
    position: transformDungeonPoint(module, socket.position, transform),
    approach: transformDungeonPoint(module, socket.approach, transform),
    seam: transformDungeonPoint(module, socket.seam, transform),
  };
}
